import React, { useState, Fragment, useRef } from "react";
import { Switch, Route } from "react-router-dom";
import moment from "moment";
import Timesheet from "../timesheet";
import "./timesheets.css";
import { parseHours } from "../../helpers/time";
import Dropdown from "../common/Dropdown/Dropdown";
import { sortByStringPropertyAlgorithm } from "../../helpers/sorting";
import { arrayNext } from "../../helpers/array";
import {
  approveTimesheet,
  loadTimesheet,
  rejectTimesheet,
  getCSV
} from "./helpers/fontApi";
import { UpdateStatus, LoadingStatus } from "./helpers/enum";
import {
  isInEditableState,
  last12Sundays,
  showableTimesheets,
  calculateTimesheetStatus
} from "./helpers/helpers";
import MobileTabletTimesheets from "./MobileTabletTimesheets/MobileTabletTimesheets";
import OrderByBar from "../orderByBar/orderByBar";
import LabeledValue from "../common/LabeledValue/LabeledValue";
import PopupModal from "../common/PopupModal/PopupModal";
import Summary from "./Summary/Summary";
import EmailTimesheet from "./EmailTimesheet/EmailTimesheet";
import BallFallAnimation from "../common/animations/BallFall/BallFall";
import LoadingError from "../common/LoadingError/LoadingError";
import { getCostCentres } from "../../helpers/apiRequests";
import { getLastCostCentreFromStorage } from "../../helpers/sessionStorage";
import DesktopTimesheets from "./DesktopTimesheets/DesktopTimesheets"; // eslint-disable-line
import Spinner from "../common/Spinner/Spinner.jsx";

export const TIMESHEETS_ORDER_BY_SESSION_STORAGE_KEY = "timesheets-order-by";

export const Rate = ({ totaledInvoices, totaledHours, isDayType }) => (
  <>
    <td className="table__column timesheets__rates">
      <span className="timesheets__rates_inner">
        <span className="timesheets__rates_inner-elem">
          £ {totaledInvoices}
        </span>
      </span>
    </td>
    <td className="table__column timesheets__rates">
      <span className="timesheets__rates_inner">
        <span className="timesheets__rates_inner-elem">
          {isDayType
            ? `${parseHours(totaledHours).split("h")[0]}d`
            : parseHours(totaledHours)}
        </span>
      </span>
    </td>
  </>
);

const TimesheetTable = ({
  rows,
  organisationTitle,
  downloadTimesheet,
  token,
  weekEnd,
  companyRef,
  downloadRef,
  loadingDownload
}) => {
  const initialRows = () =>
    rows.map(row => {
      const rowTotaledInvoices = row.rates.reduce(
        (_acc, _rateElem, index) =>
          _rateElem.invoice
            ? _acc +
              // eslint-disable-next-line
              parseInt(
                _rateElem.invoice *
                  row.standardRateTotals[index].invoiceQty *
                  100
              )
            : _acc,
        0
      );
      const rowTotaledHours = row.standardRateTotals.reduce(
        (_acc, _totalElem) =>
          _totalElem.invoiceQty ? _acc + _totalElem.invoiceQty : _acc,
        0
      );
      return {
        ...row,
        rowTotaledInvoices,
        rowTotaledHours
      };
    });

  const statusSort = (a, b) => {
    const aStatus = calculateTimesheetStatus(a);
    const bStatus = calculateTimesheetStatus(b);
    if (aStatus === "Pending") return -1;
    if (aStatus === "Rejected" && bStatus !== "Pending") return -1;
    if (aStatus === "Approved") return 1;
    return 0;
  };
  const [timesheet, setTimesheet] = useState();
  const [isPopupOpen, setPopupOpen] = useState(false);
  const togglePopup = () => setPopupOpen(!isPopupOpen);

  const hoursSort = (a, b) => (a.rowTotaledHours > b.rowTotaledHours ? 1 : -1);

  const chargeSort = (a, b) =>
    a.rowTotaledInvoices > b.rowTotaledInvoices ? 1 : -1;

  const isDayType = !!rows.filter(ts => ts.temporaryVacancyTimeUnitCode === "D")
    .length;

  const sortTimesheetsUsing = sortingAlgorithm => () =>
    setSortedRows(sortedRows.sort(sortingAlgorithm).slice()); // eslint-disable-line

  const getOrderByConfig = isDay => [
    {
      value: "Status",
      action: sortTimesheetsUsing(statusSort),
      sortingAlgorithm: statusSort
    },
    {
      value: "Worker",
      action: sortTimesheetsUsing(
        sortByStringPropertyAlgorithm("candidateName")
      ),
      sortingAlgorithm: sortByStringPropertyAlgorithm("candidateName")
    },
    {
      value: "Job title",
      action: sortTimesheetsUsing(sortByStringPropertyAlgorithm("jobTitle")),
      sortingAlgorithm: sortByStringPropertyAlgorithm("jobTitle")
    },
    {
      value: isDay ? "Days" : "Hours",
      action: sortTimesheetsUsing(hoursSort),
      sortingAlgorithm: hoursSort
    },
    {
      value: "Charge",
      action: sortTimesheetsUsing(chargeSort),
      sortingAlgorithm: chargeSort
    }
  ];

  const getDefaultOrderBy = () => {
    const savedOptionValue = sessionStorage.getItem("timesheets-order-by");
    const savedOption = getOrderByConfig(isDayType).find(
      option => option.value === savedOptionValue
    );
    return savedOption || getOrderByConfig(isDayType)[0];
  };

  const [sortedRows, setSortedRows] = useState(
    initialRows()
      .sort(getDefaultOrderBy().sortingAlgorithm)
      .slice()
  );

  const [isEmailPopUpOpen, setIsEmailPopUpOpen] = useState(false);

  const invoiceTotals = sortedRows.reduce(
    (sum, row) => sum + row.totalInvoiceAmount,
    0
  );

  const hoursTotals = parseHours(
    sortedRows.reduce((sum, row) => sum + row.rowTotaledHours, 0),
    true
  );

  const daysTotals = `${hoursTotals.split("h")[0]}days`;

  const timesheetsProps = {
    setTimesheet,
    isInEditableState,
    sortedRows,
    togglePopup,
    invoiceTotals,
    hoursTotals,
    token,
    weekEnd,
    companyRef
  };

  return (
    <div className={`table__wrapper`}>
      <div className="timesheets__header">
        <div className="timesheets__labels timesheets__header-item">
          <LabeledValue
            className="timesheets__label"
            label="total charge"
            value={`£${invoiceTotals.toFixed(2)}`}
            isBold={false}
          />
          {isDayType ? (
            <LabeledValue
              className="timesheets__label"
              label="total days"
              value={daysTotals}
              isBold={false}
            />
          ) : (
            <LabeledValue
              className="timesheets__label"
              label="total hours"
              value={hoursTotals}
              isBold={false}
            />
          )}
        </div>
        <h4 className="timesheets__title timesheets__header-item tw-text-2xl tw-font-bold">
          {organisationTitle}
        </h4>
        <span className="timesheets__orderby timesheets__header-item">
          <div
            className={`d-flex flex-column timesheets__orderby-bar__wrapper`}
          >
            <div className="d-block d-sm-none">
              <b>Order by</b>
            </div>
            <OrderByBar
              titleClass={"d-none d-sm-block d-md-block d-lg-block d-xl-block"}
              className="timesheets__orderby-bar"
              selectConfig={getOrderByConfig(isDayType)}
              defaultValue={getDefaultOrderBy().value}
              selectedOptionSessionStorageKey={
                TIMESHEETS_ORDER_BY_SESSION_STORAGE_KEY
              }
            />
          </div>
        </span>
      </div>
      {isPopupOpen && (
        <PopupModal>
          <Summary
            timesheet={timesheet}
            closeHandler={togglePopup}
            sortedRows={sortedRows}
          />
        </PopupModal>
      )}
      <DesktopTimesheets
        className="d-none d-xl-table"
        {...timesheetsProps}
        isDayType={isDayType}
      />
      <MobileTabletTimesheets
        className="d-sm-block d-md-block d-lg-block d-xl-none"
        {...timesheetsProps}
        isDayType={isDayType}
      />
      <div className="d-none" />
      <div className="tw-flex tw-justify-end tw-px-6 tw-py-4 tw-flex-col lg:tw-flex-row">
        <a className="d-none" ref={downloadRef}></a>
        <button
          disabled={loadingDownload}
          onClick={() => downloadTimesheet(weekEnd)}
          className="btn btn-primary timesheets__viewButton tw-flex tw-justify-around lg:tw-mr-2 mb-2"
        >
          <p className="tw-mr-2  ">Download Timesheet Summary</p>
          {loadingDownload && <Spinner />}
        </button>
        <button
          onClick={() => setIsEmailPopUpOpen(!isEmailPopUpOpen)}
          className="btn btn-primary timesheets__viewButton  "
        >
          Send Timesheet Summary via Email
        </button>
        {isEmailPopUpOpen && (
          <PopupModal>
            <EmailTimesheet
              weekEnd={weekEnd}
              companyRef={companyRef}
              getCSV={getCSV}
              setIsEmailPopUpOpen={setIsEmailPopUpOpen}
            />
          </PopupModal>
        )}
      </div>
    </div>
  );
};

const SplitOrganisationTables = ({
  rows,
  downloadTimesheet,
  token,
  weekEnd,
  downloadRef,
  companyRef,
  loadingDownload
}) => {
  const onlyUnique = (value, index, self) => self.indexOf(value) === index;
  const organisations = rows
    .map(row => row.organisationTitle)
    .filter(onlyUnique)
    .sort();

  return organisations.map(organisationTitle => {
    const orgTimesheets = rows.filter(
      row => row.organisationTitle === organisationTitle
    );
    return (
      <Fragment key={organisationTitle}>
        <TimesheetTable
          loadingDownload={loadingDownload}
          token={token}
          downloadRef={downloadRef}
          weekEnd={weekEnd}
          companyRef={companyRef}
          rows={orgTimesheets}
          organisationTitle={organisationTitle}
          downloadTimesheet={downloadTimesheet}
        />
      </Fragment>
    );
  });
};

const updateSheet = (rows, setRows, timesheetRef, updatedObj) =>
  setRows(
    rows.map(r =>
      r.timesheetRef === timesheetRef ? { ...r, ...updatedObj } : r
    )
  );

const Timesheets = ({ companyRef, tokenStore, handleOverlay, passImage }) => {
  const token = tokenStore.getToken();
  const user = tokenStore.getUser();
  const userEmail = user.emails[0];
  const [rows, setRows] = useState([]);
  const [loadingState, setLoadingState] = useState(LoadingStatus.Initial);
  const [updateState, setUpdateState] = useState(UpdateStatus.Hidden);
  const [currentWeek, setWeek] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [pickedWeek, setPickedWeek] = useState("");
  const [costCentres, setCostCentres] = useState();
  const [pickedCostCentre, setPickedCostCentre] = useState(
    getLastCostCentreFromStorage()
  );
  const hasCostCentres = costCentres && costCentres.length > 0;
  const hasNotPickedCostCentre = hasCostCentres && !pickedCostCentre;
  const initCostCentres = async () => {
    try {
      const resp = await getCostCentres(token);
      setCostCentres(resp.costCenters);
    } catch (err) {
      console.error(`Error while getting cost centres: ${err}`);
    }
  };
  const downloadRef = useRef();
  const [loadingDownload, setLoadingDownload] = useState(false);

  const loadWeek = weekEnd => {
    const weekEndDate = weekEnd.split("T")[0];
    setWeek(weekEndDate);
    setRows([]);
    setLoadingState(LoadingStatus.Loading);
    loadTimesheet(
      weekEndDate,
      companyRef,
      userEmail,
      setRows,
      setLoadingState,
      initCostCentres
    );
  };

  const downloadTimesheet = async weekEndDate => {
    setLoadingDownload(true);
    const response = await getCSV(companyRef, userEmail, weekEndDate);
    if (response.status === 200) {
      const blob = new Blob([response.data], {
        type: "text/csv"
      });
      downloadRef.current.href = window.URL.createObjectURL(blob);
      downloadRef.current.download = `Timesheet summary for week ending ${new Date(
        pickedWeek
      )
        .toLocaleDateString("en-GB")
        .replace(/\//g, ".")}.csv`;
      downloadRef.current.click();
    }
    setLoadingDownload(false);
  };

  return (
    <div style={{ paddingBottom: "28rem" }}>
      <Switch>
        <Route exact path="/timesheets">
          <div className="d-flex w-100 justify-content-center flex-column align-items-center">
            <h2 className="tw-text-2xl tw-mb-4 tw-font-bold">
              Workers' Timesheets
            </h2>
            <span>
              <b className="mr-1">Week ending</b>
              <Dropdown
                onChange={e => {
                  loadWeek(e.target.value);
                  setPickedWeek(e.target.value);
                }}
                defaultValue={new Date(pickedWeek).toLocaleDateString("en-GB")}
                className="mb-2"
              >
                <option value="">Select date...</option>
                {last12Sundays().map(d => (
                  <option value={moment(d).format("YYYY-MM-DD")}>
                    {d.toLocaleDateString("en-GB")}
                  </option>
                ))}
              </Dropdown>
            </span>
          </div>
          <div className="d-flex justify-content-center flex-column align-items-center timesheets__wrapper">
            {loadingState === LoadingStatus.Loading && (
              <p className="timesheets__loading">
                <BallFallAnimation />
              </p>
            )}
            {loadingState === LoadingStatus.Error && (
              <LoadingError
                retryFunc={() => loadWeek(pickedWeek)}
                errorText={"There has been an error in loading the timesheets."}
              />
            )}
            {loadingState === LoadingStatus.Success && currentWeek && (
              <Fragment>
                <ul className="nav nav-tabs mb-1"></ul>
                {showableTimesheets(rows).length > 0 ? (
                  <>
                    <SplitOrganisationTables
                      token={token}
                      companyRef={companyRef}
                      loadingDownload={loadingDownload}
                      weekEnd={pickedWeek}
                      downloadTimesheet={downloadTimesheet}
                      rows={showableTimesheets(rows)}
                      downloadRef={downloadRef}
                    />
                  </>
                ) : (
                  <h2 className="tw-text-center tw-font-bold tw-text-xl">
                    There are no timesheets available for this week
                  </h2>
                )}
              </Fragment>
            )}
          </div>
        </Route>
        <Route
          exact
          path="/timesheets/:timesheetId"
          render={({ match, location }) => {
            const { state } = location;
            const { sortedTimesheetRefs } = state;
            const timesheetRef = parseInt(match.params.timesheetId, 10);
            const nextTimesheet = arrayNext(
              sortedTimesheetRefs,
              sortedTimesheetRefs.indexOf(timesheetRef)
            );

            const timesheetShown = rows.find(
              sheet => sheet.timesheetRef === timesheetRef
            );
            return (
              <Timesheet
                companyRef={companyRef}
                tokenStore={tokenStore}
                passImage={passImage}
                handleOverlay={handleOverlay}
                updateState={updateState}
                costCentres={costCentres}
                setUpdateState={setUpdateState}
                timesheet={timesheetShown}
                sortedTimesheetRefs={sortedTimesheetRefs || []}
                nextTimesheet={nextTimesheet}
                pickedCostCentre={pickedCostCentre}
                setPickedCostCentre={setPickedCostCentre}
                hasCostCentres={hasCostCentres}
                hasNotPickedCostCentre={hasNotPickedCostCentre}
                approveTimesheet={() =>
                  approveTimesheet(
                    timesheetShown.timesheetRef,
                    setUpdateState,
                    updateSheet,
                    rows,
                    setRows,
                    userEmail,
                    pickedCostCentre
                  )
                }
                loadWeek={loadWeek}
                currentWeek={pickedWeek}
                rejectTimesheet={() => {
                  rejectTimesheet(
                    timesheetShown.timesheetRef,
                    setUpdateState,
                    updateSheet,
                    rows,
                    setRows,
                    rejectionReason,
                    userEmail
                  );
                }}
                setRejectionReason={setRejectionReason}
                rejectionReason={rejectionReason}
                downloadTimesheet={downloadTimesheet}
              />
            );
          }}
        />
      </Switch>
    </div>
  );
};

export default Timesheets;
